import React, { useState, useCallback, useRef, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";

import TimeInput24H from "../../../components/TimeInput24H";
import pestReportService from "../../../api/pestReportService";
import { formatServiceDate, resolveBusinessDateTime } from "../../../utils/dateUtils";

const PEST_LEVELS = [
  { value: 0, label: "0", color: "#15803D" },
  { value: 1, label: "1", color: "#A16207" },
  { value: 2, label: "2", color: "#C2410C" },
  { value: 3, label: "3", color: "#B91C1C" },
];

/* ───────────────────────────── */
/* Selector nivel */
/* ───────────────────────────── */

const PestLevelSelector = ({ value, onChange, disabled }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: 0.6,
      p: 0.4,
      borderRadius: "10px",
      bgcolor: "#F3F4F6",
    }}
  >
    {PEST_LEVELS.map((level) => {
      const selected = value === level.value;
      return (
        <Box
          key={level.value}
          component="button"
          type="button"
          disabled={disabled}
          onClick={() => onChange(level.value)}
          sx={{
            all: "unset",
            cursor: disabled ? "default" : "pointer",
            textAlign: "center",
            py: 0.85,
            borderRadius: "8px",
            bgcolor: selected ? "#fff" : "transparent",
            color: selected ? level.color : "#6B7280",
            fontSize: 14,
            fontWeight: 700,
            boxShadow: selected ? "0 1px 3px rgba(15,23,42,0.08)" : "none",
            transition: "all 0.15s ease",
            fontFamily: "inherit",
          }}
        >
          {level.label}
        </Box>
      );
    })}
  </Box>
);

/* ───────────────────────────── */
/* Dropzone PDF */
/* ───────────────────────────── */

const PdfDropZone = ({
  file,
  onFile,
  onRemove,
  disabled,
  error,
}) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef();

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);

      if (disabled) return;

      const f = e.dataTransfer.files?.[0];

      if (f) onFile(f);
    },
    [disabled, onFile]
  );

  const handleDragOver = (e) => {
    e.preventDefault();

    if (!disabled) setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  if (file) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          p: 1.1,
          borderRadius: "10px",
          border: "1px solid #E5E7EB",
          bgcolor: "#fff",
        }}
      >
        <PictureAsPdfIcon
          sx={{
            color: "#dc2626",
            fontSize: 28,
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 12.5,
              fontWeight: 600,
              color: "#111",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {file.name}
          </Typography>

          <Typography
            sx={{
              fontSize: 11,
              color: "#9CA3AF",
            }}
          >
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </Typography>
        </Box>

        <Tooltip title="Quitar PDF">
          <IconButton
            size="small"
            onClick={onRemove}
            disabled={disabled}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={() =>
        !disabled && inputRef.current?.click()
      }
      sx={{
        borderRadius: "10px",
        border: `1px dashed ${
          dragging ? "#111" : error ? "#B91C1C" : "#D1D5DB"
        }`,
        bgcolor: dragging ? "#F9FAFB" : "#FAFAFA",
        p: 2,
        textAlign: "center",
        cursor: disabled ? "default" : "pointer",
        transition: "all 0.15s ease",
      }}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        hidden
        onChange={(e) =>
          onFile(e.target.files?.[0])
        }
      />

      <CloudUploadIcon
        sx={{
          fontSize: 26,
          color: dragging ? "#111" : "#C4C8CE",
        }}
      />

      <Typography
        sx={{
          mt: 0.8,
          fontSize: 12.5,
          color: "#6B7280",
          fontWeight: 600,
        }}
      >
        Arrastra PDF o selecciona archivo
      </Typography>

      <Typography
        sx={{
          mt: 0.5,
          fontSize: 11,
          color: "#9ca3af",
        }}
      >
        Máx 10MB
      </Typography>
    </Box>
  );
};

/* ───────────────────────────── */
/* PDF Preview */
/* ───────────────────────────── */

const PdfPreview = ({ file }) => {
  const [url, setUrl] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!file) {
      setUrl(null);
      setLoaded(false);
      return;
    }

    const objUrl = URL.createObjectURL(file);

    setUrl(objUrl);
    setLoaded(false);

    return () => URL.revokeObjectURL(objUrl);
  }, [file]);

  if (!url) return null;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        bgcolor: "#111827",
        overflow: "hidden",
      }}
    >
      {!loaded && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "#111827",
            zIndex: 2,
          }}
        >
          <CircularProgress
            size={28}
            sx={{ color: "#6366f1" }}
          />
        </Box>
      )}

      <iframe
        src={url}
        title="PDF Preview"
        onLoad={() => setLoaded(true)}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.2s ease",
          background: "#111827",
        }}
      />
    </Box>
  );
};

/* ───────────────────────────── */
/* MAIN COMPONENT */
/* ───────────────────────────── */

export const CreatePestReportModal = ({
  open,
  service,
  onClose,
  onSuccess,
}) => {
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    pestLevel: null,
    entryTime: "",
    exitTime: "",
    reportFile: null,
  });

  const handleFieldChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setError(null);
  };

  const handleFile = useCallback((file) => {
    if (!file) return;

    if (file.type !== "application/pdf") {
      setError("Solo PDFs");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Máx 10MB");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      reportFile: file,
    }));

    setError(null);
  }, []);

  const handleRemoveFile = () => {
    setFormData((prev) => ({
      ...prev,
      reportFile: null,
    }));
  };

  const calculateDuration = () => {
    if (!formData.entryTime || !formData.exitTime) return null;

    const parseMinutes = (time) => {
      if (!time || !time.includes(":")) return null;

      const [h, m] = time.split(":").map(Number);

      if (Number.isNaN(h) || Number.isNaN(m)) return null;

      return h * 60 + m;
    };

    const entry = parseMinutes(formData.entryTime);
    let exit = parseMinutes(formData.exitTime);

    if (entry === null || exit === null) return null;

    // Cruza medianoche
    if (exit <= entry) {
      exit += 24 * 60;
    }

    const diff = exit - entry;

    if (diff <= 0) return null;

    const hours = Math.floor(diff / 60);
    const mins = diff % 60;

    return `${hours}h ${mins}m`;
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      setLoading(true);

      if (!service) {
        setError("No hay evento seleccionado");
        return;
      }

      if (formData.pestLevel === null || formData.pestLevel === undefined) {
        setError("Debe seleccionar un nivel de plaga");
        return;
      }

      if (!formData.entryTime) {
        setError("La hora de entrada es requerida");
        return;
      }

      if (!formData.exitTime) {
        setError("La hora de salida es requerida");
        return;
      }

      if (!formData.reportFile) {
        setError("Debe adjuntar el PDF del reporte");
        return;
      }

      const validateTime = (time) => {
        if (!time || !time.includes(":")) return false;

        const [h, m] = time.split(":").map(Number);

        if (
          Number.isNaN(h) ||
          Number.isNaN(m) ||
          h < 0 ||
          h > 23 ||
          m < 0 ||
          m > 59
        ) {
          return false;
        }

        return true;
      };

      if (!validateTime(formData.entryTime)) {
        setError("Hora de entrada inválida");
        return;
      }

      if (!validateTime(formData.exitTime)) {
        setError("Hora de salida inválida");
        return;
      }

      const entryTime = resolveBusinessDateTime(formData.entryTime);

      let exitTime = resolveBusinessDateTime(formData.exitTime);

      // Detectar cruce de medianoche / turno de madrugada
      if (exitTime <= entryTime) {
        exitTime.setDate(exitTime.getDate() + 1);
      }

      const durationMs = exitTime - entryTime;
      const durationHours = durationMs / (1000 * 60 * 60);

      if (durationHours <= 0) {
        setError("Duración inválida");
        return;
      }

      // Protección anti errores absurdos
      if (durationHours > 16) {
        setError("La duración excede el máximo permitido");
        return;
      }

      const eventId = service._id || service.id;

      if (!eventId) {
        setError("El evento no tiene un ID válido");
        return;
      }

      const result = await pestReportService.createPestReport({
        eventId,
        serviceId: service.serviceId || service._id || service.id,
        pestLevel: formData.pestLevel,
        entryTime: entryTime.toISOString(),
        exitTime: exitTime.toISOString(),
        reportPdf: formData.reportFile,
      });

      if (onSuccess) {
        onSuccess(result.report);
      }

      setFormData({
        pestLevel: null,
        entryTime: "",
        exitTime: "",
        reportFile: null,
      });

      onClose();
    } catch (err) {
      setError(err.message || "Error al crear el reporte");
    } finally {
      setLoading(false);
    }
  };

  const duration = calculateDuration();

  const isValid =
    formData.pestLevel !== null &&
    formData.entryTime &&
    formData.exitTime &&
    formData.reportFile;

  return (
    <Dialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: "min(920px, calc(100vw - 32px))",
          height: "min(640px, calc(100dvh - 32px))",
          maxWidth: "none",
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow: "0 24px 64px rgba(15,23,42,0.18)",
        },
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "340px 1fr" },
          height: "100%",
        }}
      >
        <Box
          sx={{
            borderRight: "1px solid #EEE",
            display: "flex",
            flexDirection: "column",
            bgcolor: "#fff",
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.6,
              borderBottom: "1px solid #EEE",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box minWidth={0}>
              <Typography sx={{ fontWeight: 600, fontSize: 15, color: "#111", letterSpacing: "-0.02em" }}>
                Crear reporte
              </Typography>
              <Typography
                noWrap
                sx={{ mt: 0.3, fontSize: 12.5, color: "#6B7280" }}
              >
                {service?.branch?.name || service?.client?.name || service?.name || "Servicio"}
              </Typography>
              <Typography sx={{ mt: 0.15, fontSize: 11.5, color: "#9CA3AF" }}>
                {[
                  service?.isoDate || service?.date
                    ? formatServiceDate(service.isoDate || service.date, "es-MX", {
                        day: "numeric",
                        month: "short",
                      })
                    : null,
                  service?.hour,
                  service?.type,
                ]
                  .filter(Boolean)
                  .join("  ·  ")}
              </Typography>
            </Box>
            <IconButton
              onClick={onClose}
              disabled={loading}
              size="small"
              sx={{ color: "#6B7280" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 1.5, borderRadius: "8px", fontSize: 13 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={2}>
              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#8A8F98", mb: 0.8 }}>
                  Nivel de plaga
                </Typography>
                <PestLevelSelector
                  value={formData.pestLevel}
                  onChange={(v) => handleFieldChange("pestLevel", v)}
                  disabled={loading}
                />
              </Box>

              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#8A8F98", mb: 0.8 }}>
                  Horario
                </Typography>
                <Stack direction="row" spacing={1}>
                  <TimeInput24H
                    fullWidth
                    value={formData.entryTime}
                    onChange={(e) => handleFieldChange("entryTime", e.target.value)}
                    disabled={loading}
                    variant="outlined"
                    size="small"
                    label="Entrada"
                    InputProps={{
                      startAdornment: (
                        <AccessTimeIcon sx={{ mr: 0.75, fontSize: 16, color: "#9CA3AF" }} />
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                  <TimeInput24H
                    fullWidth
                    value={formData.exitTime}
                    onChange={(e) => handleFieldChange("exitTime", e.target.value)}
                    disabled={loading}
                    variant="outlined"
                    size="small"
                    label="Salida"
                    InputProps={{
                      startAdornment: (
                        <AccessTimeIcon sx={{ mr: 0.75, fontSize: 16, color: "#9CA3AF" }} />
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "8px" } }}
                  />
                </Stack>
                {duration ? (
                  <Typography sx={{ mt: 0.7, fontSize: 12, color: "#6B7280" }}>
                    Duración {duration}
                  </Typography>
                ) : null}
              </Box>

              <Box>
                <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: "#8A8F98", mb: 0.8 }}>
                  PDF del reporte
                </Typography>
                <PdfDropZone
                  file={formData.reportFile}
                  onFile={handleFile}
                  onRemove={handleRemoveFile}
                  disabled={loading}
                  error={!!error && !formData.reportFile}
                />
              </Box>
            </Stack>
          </Box>

          <Box sx={{ p: 1.5, borderTop: "1px solid #EEE" }}>
            <Button
              fullWidth
              onClick={handleSubmit}
              disabled={loading || !isValid}
              variant="contained"
              disableElevation
              startIcon={
                loading ? (
                  <CircularProgress size={14} sx={{ color: "white" }} />
                ) : (
                  <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                )
              }
              sx={{
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                borderRadius: "8px",
                py: 1,
                bgcolor: "#111",
                "&:hover": { bgcolor: "#222" },
              }}
            >
              {loading ? "Guardando..." : "Guardar reporte"}
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            bgcolor: "#F4F4F5",
            overflow: "hidden",
            flexDirection: "column",
          }}
        >
          {formData.reportFile ? (
            <PdfPreview file={formData.reportFile} />
          ) : (
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#9CA3AF",
                px: 3,
                textAlign: "center",
              }}
            >
              <PictureAsPdfIcon sx={{ fontSize: 40, mb: 1.2, opacity: 0.5 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#6B7280" }}>
                Vista previa del PDF
              </Typography>
              <Typography sx={{ mt: 0.4, fontSize: 12.5 }}>
                Adjunta el archivo para verlo aquí
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};